// @ts-strict-ignore
import { DashboardCard } from "@dashboard/components/Card";
import { ProductErrorFragment } from "@dashboard/graphql";
import { getFormErrors, getProductErrorMessage } from "@dashboard/utils/errors";
import createNonNegativeValueChangeHandler from "@dashboard/utils/handlers/nonNegativeValueChangeHandler";
import { Box, Input, Text } from "@saleor/macaw-ui-next";
import * as React from "react";
import { useIntl } from "react-intl";

interface ProductShippingProps {
  data: {
    weight: string;
    heightCm?: string;
    widthCm?: string;
    lengthCm?: string;
  };
  disabled: boolean;
  errors: ProductErrorFragment[];
  weightUnit: string;
  onChange: (event: React.ChangeEvent<any>) => void;
  // When set, the box-dimension inputs (height/width/length) are shown. Used on
  // the variant detail page; product-level shipping only shows weight.
  showDimensions?: boolean;
  // Optional read-only volumetric weight, formatted as a string (e.g. "0.13 kg").
  volumetricWeight?: string;
}

export const ProductShipping = (props: ProductShippingProps) => {
  const { data, disabled, errors, weightUnit, onChange, showDimensions, volumetricWeight } = props;
  const intl = useIntl();
  const formErrors = getFormErrors(["weight"], errors);
  const handleChange = createNonNegativeValueChangeHandler(onChange);

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage({
            id: "3rIMq/",
            defaultMessage: "Shipping",
            description: "product shipping",
          })}
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box __width="25%">
            <Input
              disabled={disabled}
              label={intl.formatMessage({
                id: "SUbxSK",
                defaultMessage: "Weight",
                description: "product weight",
              })}
              error={!!formErrors.weight}
              helperText={getProductErrorMessage(formErrors.weight, intl)}
              name="weight"
              type="number"
              size="small"
              value={data.weight}
              onChange={handleChange}
              endAdornment={<Text marginRight={2}>{weightUnit || ""}</Text>}
            />
          </Box>
          {showDimensions && (
            <>
              <Box __width="20%">
                <Input
                  disabled={disabled}
                  label={intl.formatMessage({
                    id: "JM9hZy",
                    defaultMessage: "Height",
                    description: "variant box height",
                  })}
                  name="heightCm"
                  type="number"
                  size="small"
                  value={data.heightCm ?? ""}
                  onChange={handleChange}
                  endAdornment={<Text marginRight={2}>cm</Text>}
                />
              </Box>
              <Box __width="20%">
                <Input
                  disabled={disabled}
                  label={intl.formatMessage({
                    id: "mnUZDn",
                    defaultMessage: "Width",
                    description: "variant box width",
                  })}
                  name="widthCm"
                  type="number"
                  size="small"
                  value={data.widthCm ?? ""}
                  onChange={handleChange}
                  endAdornment={<Text marginRight={2}>cm</Text>}
                />
              </Box>
              <Box __width="20%">
                <Input
                  disabled={disabled}
                  label={intl.formatMessage({
                    id: "2y9ZDc",
                    defaultMessage: "Length",
                    description: "variant box length",
                  })}
                  name="lengthCm"
                  type="number"
                  size="small"
                  value={data.lengthCm ?? ""}
                  onChange={handleChange}
                  endAdornment={<Text marginRight={2}>cm</Text>}
                />
              </Box>
            </>
          )}
        </Box>
        {showDimensions && volumetricWeight && (
          <Box marginTop={3}>
            <Text size={2} color="default2">
              {intl.formatMessage({
                id: "aG/nn2",
                defaultMessage: "Volumetric weight",
                description: "variant volumetric weight",
              })}
              {": "}
              {volumetricWeight}
            </Text>
          </Box>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};
